import { t } from 'i18next';
import GolfIcon from '../GolfIcon';
import { PlayerStats } from '@/types'; // Adjusted import path
import { cn } from '@/lib/utils';

interface ScoreDistributionProps {
  stats: PlayerStats;
  className?: string;
}

function ScoreDistribution({ stats, className }: ScoreDistributionProps) {
  const totalHoles = stats.birdies + stats.pars + stats.bogeys + stats.doubleBogeys + stats.eagles;

  return (
    <div className={cn("bg-card rounded-lg shadow-sm p-4", className)}>
      <div className="flex items-center gap-2">
        <GolfIcon name="chart" className="size-5" />
        <span className="text-sm font-semibold">{t('scores_distribution')}</span>
      </div>
      <div className="relative my-2">
        {stats.birdies == 0 && stats.pars == 0 && stats.bogeys == 0 && stats.doubleBogeys == 0 && stats.eagles == 0 ?
          <div className="absolute inset-0 items-center justify-center bg-card/80 z-50 text-center p-4 flex flex-col">
            <p className="text-muted-foreground text-sm">{t('no_data')}</p>
            <p className="text-muted-foreground text-sm">{t('continue_play_see_stats')}</p>
          </div> : ''}
        <div className="flex justify-between border-b items-end h-40 mb-2 relative">
          {/* Reference lines */}
          <div className="absolute left-0 right-0 h-[25%] bottom-[75%] border-b border-dashed border-gray-200 dark:border-gray-700 w-full"></div>
          <div className="absolute left-0 right-0 h-[50%] bottom-[50%] border-b border-dashed border-gray-200 dark:border-gray-700 w-full"></div>
          <div className="absolute left-0 right-0 h-[75%] bottom-[25%] border-b border-dashed border-gray-200 dark:border-gray-700 w-full"></div>
          {/* Eagle Bar */}
          <div className="flex flex-col items-center w-1/5 z-10 opacity-60">
            <div className="relative w-12 flex justify-center">
              <div
                className="w-10 bg-purple-500 bg-gradient-to-t from-purple-200 dark:from-purple-800 to-purple-500 rounded-t-sm"
                style={{
                  height: `${Math.max(5, (stats.eagles / totalHoles) * 100 * 1.5)}px`,
                }}
              ></div>
            </div>
          </div>

          {/* Birdie Bar */}
          <div className="flex flex-col items-center w-1/5 z-10 opacity-60">
            <div className="relative w-12 flex justify-center">
              <div
                className="w-10 bg-blue-500 bg-gradient-to-t from-blue-200 dark:from-blue-800 to-blue-500 rounded-t-sm"
                style={{
                  height: `${Math.max(5, (stats.birdies / totalHoles) * 100 * 1.5)}px`,
                }}
              ></div>
            </div>
          </div>

          {/* Par Bar */}
          <div className="flex flex-col items-center w-1/5 z-10 opacity-60">
            <div className="relative w-12 flex justify-center">
              <div
                className="w-10 bg-green-500 bg-gradient-to-t from-green-200 dark:from-green-800 to-green-500 rounded-t-sm"
                style={{
                  height: `${Math.max(5, (stats.pars / totalHoles) * 100 * 1.5)}px`,
                }}
              ></div>
            </div>
          </div>

          {/* Bogey Bar */}
          <div className="flex flex-col items-center w-1/5 z-10 opacity-60">
            <div className="relative w-12 flex justify-center">
              <div
                className="w-10 bg-orange-500 bg-gradient-to-t from-orange-200 dark:from-orange-800 to-orange-500 rounded-t-sm"
                style={{
                  height: `${Math.max(5, (stats.bogeys / totalHoles) * 100 * 1.5)}px`,
                }}
              ></div>
            </div>
          </div>

          {/* Double+ Bar */}
          <div className="flex flex-col items-center w-1/5 z-10 opacity-60">
            <div className="relative w-12 flex justify-center">
              <div
                className="w-10 bg-red-500 bg-gradient-to-t from-red-200 dark:from-red-800 to-red-500 rounded-t-sm"
                style={{
                  height: `${Math.max(5, (stats.doubleBogeys / totalHoles) * 100 * 1.5)}px`,
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-2">
          <div className="text-center w-1/5">
            <div className="text-xs font-medium text-purple-600">Eagle</div>
            <div className="text-xs text-gray-500">{stats.eagles ? Math.round((stats.eagles / totalHoles) * 100) + '%' : '-'}</div>
          </div>
          <div className="text-center w-1/5">
            <div className="text-xs font-medium text-blue-600">Birdie</div>
            <div className="text-xs text-gray-500">{stats.birdies ? Math.round((stats.birdies / totalHoles) * 100) + '%' : '-'}</div>
          </div>
          <div className="text-center w-1/5">
            <div className="text-xs font-medium text-green-600">Par</div>
            <div className="text-xs text-gray-500">{stats.pars ? Math.round((stats.pars / totalHoles) * 100) + '%' : '-'}</div>
          </div>
          <div className="text-center w-1/5">
            <div className="text-xs font-medium text-orange-600">Bogey</div>
            <div className="text-xs text-gray-500">{stats.bogeys ? Math.round((stats.bogeys / totalHoles) * 100) + '%' : '-'}</div>
          </div>
          <div className="text-center w-1/5">
            <div className="text-xs font-medium text-red-600">Double</div>
            <div className="text-xs text-gray-500">{stats.doubleBogeys ? Math.round((stats.doubleBogeys / totalHoles) * 100) + '%' : '-'}</div>
          </div>
        </div>
      </div>
    </div >
  );
}

export default ScoreDistribution;
